import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCircleDot } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import aspects from '../../../questions/astrology/aspects.json';
import Question from '../../../components/Question';
import sextileSymbol from '../../../../assets/symbols/sextile.svg';

const SextileLesson = ({ level = 'beginner', onNextLesson, markLessonComplete, currentQuestion = 0, onQuestionChange }) => {
  const chapter = aspects.find(c => c.chapter === "Chapter 6: Aspects and Transits");
  const lessonData = chapter ? chapter.lessons.find(l => l.title === "Lesson 6: The Sextile (⚹ 60°)") : null;

  const [completedQuestions, setCompletedQuestions] = useState(lessonData ? Array(lessonData.sections.length).fill(false) : []);
  const [feedback, setFeedback] = useState(null);

  if (!lessonData || !lessonData.sections) {
    return <div>Error: Lesson data not found. Please check the JSON structure.</div>;
  }

  const handleQuestionChange = (newQuestionIndex) => {
    onQuestionChange(newQuestionIndex);
    setFeedback(null);
  };

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      setFeedback('Correct!');
      const newCompletedQuestions = [...completedQuestions];
      newCompletedQuestions[currentQuestion] = true;
      setCompletedQuestions(newCompletedQuestions);
      
      setTimeout(() => {
        if (currentQuestion === lessonData.sections.length - 1) {
          markLessonComplete();
          onNextLesson();
        } else {
          handleQuestionChange(currentQuestion + 1);
        }
      }, 1500);
    } else {
      const currentQuestionData = lessonData.sections[currentQuestion].questions.find(q => q.level === level);
      setFeedback(`Incorrect. The correct answer is: ${currentQuestionData.correctAnswer}`);
      setTimeout(() => {
        setFeedback(null);
      }, 3000);
    }
  };

  const handleDotClick = (index) => {
    if (index <= currentQuestion || completedQuestions[index]) {
      handleQuestionChange(index);
    }
  };

  const transition = { duration: 0.15, ease: "easeInOut" };
  const currentSectionData = lessonData.sections[currentQuestion];
  const currentQuestionData = currentSectionData.questions.find(q => q.level === level);

  return (
    <div>
      <Helmet>
        <title>{`${currentSectionData.title}`}</title>
      </Helmet>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          <section className="section has-text-centered" id={currentSectionData.id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <div className="aspect-symbol mb-4">
                    <img src={sextileSymbol} alt="Sextile aspect symbol" style={{ height: '4rem', width: '4rem', filter: 'drop-shadow(0 0 6px #ffdd57)' }} />
                  </div>
                  <h2 className="has-text-primary">{currentSectionData.title}</h2>
                  <p className="has-text-white">{currentSectionData.content}</p>
                </div>
              </div>
            </div>
          </section>
          <section className="section has-text-centered">
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <Question
                    question={currentQuestionData.question}
                    options={currentQuestionData.options}
                    correctAnswer={currentQuestionData.correctAnswer}
                    onAnswered={handleAnswer}
                  />
                </div>
              </div>
            </div>
          </section>
          <section className="section has-text-centered">
            <div className="column">
              <div className="dots mt-4">
                {lessonData.sections.map((_, index) => (
                  <FontAwesomeIcon
                    key={index}
                    icon={completedQuestions[index] ? faCircleDot : faCircle}
                    className={`mr-2 ${index === currentQuestion ? 'has-text-primary' : completedQuestions[index] ? 'has-text-warning' : 'has-text-grey'} dot-icon`}
                    onClick={() => handleDotClick(index)}
                  />
                ))}
              </div>
              <div className="mt-2">
                <p className="has-text-warning has-text-weight-bold">
                  {completedQuestions.filter(Boolean).length} of {lessonData.sections.length} questions in this lesson completed
                </p>
              </div>
            </div>
          </section>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default SextileLesson;